import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Container } from 'Components/Container';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressStepper from '../../../../Components/ProggessStepper';
import { Card } from '../../../../Components/Card';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAuthTenant } from '../../../../store/auth';
import {
  MenuItem, FormControl, Select, InputLabel, Box, Typography,
  Button, CircularProgress, List, ListItem, ListItemText, FormHelperText,
} from '@mui/material';
import { getBreadcrumbBase, options, typeLabels } from '../../../../utils/labels_utils';
import { useQuery } from 'react-query';
import { getGapAnalysisControlsDetails, postGapAnalysisVinculo } from 'services/gap-analysis';
import ItemSelector from '../../../../Components/ItemSelector';
import { PageHelper } from '../../../../Components/PageHelper';


export const GapAnalysisControlsLinkShow = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const tenant = selectedTenant.slug;

  const tourSteps = useMemo(() => [
    { target: 'body', content: t('gap-analysis:controlsLink.tour.step0'), placement: 'center', disableBeacon: true },
    { target: '[data-tour="page-title"]', content: t('gap-analysis:controlsLink.tour.step1'), disableScrolling: true },
    { target: '[data-tour="stepper"]', content: t('gap-analysis:controlsLink.tour.step2'), disableScrolling: true },
    { target: '[data-tour="entity-type-select"]', content: t('gap-analysis:controlsLink.tour.step3'), disableScrolling: true },
    { target: '[data-tour="entity-selector"]', content: t('gap-analysis:controlsLink.tour.step4') },
    { target: '[data-tour="next-button"]', content: t('gap-analysis:controlsLink.tour.step5'), disableScrolling: true },
    { target: 'body', content: t('gap-analysis:controlsLink.tour.step6'), placement: 'center' },
    { target: 'body', content: t('gap-analysis:controlsLink.tour.step7'), placement: 'center' },
    { target: 'body', content: t('gap-analysis:controlsLink.tour.step8'), placement: 'center' },
  ], [t]);
  const [searchTerm, setSearchTerm] = useState("");
  const [controlSearchTerm, setControlSearchTerm] = useState("");
  const [items, setItems] = useState([]);
  const { id } = useParams();
  const [selectedOption, setSelectedOption] = useState("company");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const breadcrumbBase = getBreadcrumbBase(location.pathname);

  const {
    isLoading,
    error,
    data: gapAnalysis,
  } = useQuery('getGapAnalysisControlsDetails', async () => {
    const data = await getGapAnalysisControlsDetails(id);
    return data.status === 200 ? data.data : null;
  });

  const { register,
    handleSubmit,
    formState: { errors }, watch, setValue, trigger } = useForm({
    defaultValues: {
      company_id: selectedTenant._id || "",
      type: "companies",
      entity: [],
      controls: [],
    }
  });

  useEffect(() => {
    if (!selectedOption || selectedOption === "companies") {
      setItems(
        selectedTenant
          ? [{ _id: selectedTenant._id, name: selectedTenant.name, description: selectedTenant.description, type: 'companies' }]
          : []
      );
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const selectedObj = options.find((opt) => opt.value === selectedOption);
        if (!selectedObj || !selectedObj.fetchFunction) return;
        const response = await selectedObj.fetchFunction(tenant);
        const itemsWithType = response.data.map((item) => ({
          ...item,
          type: selectedObj.value,
        }));
        setItems(itemsWithType || []);
      } catch (error) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData()
  }, [selectedOption, tenant, selectedTenant]);



  const submitVinculos = handleSubmit(async (data) => {
    try {
      const formattedData = {
        ...data,
        entity: data.entity.map((item) => ({
          id: item._id,
          type: item.type,
        })),
        controls: data.controls.map((item) => ({
          id: item._id,
          frameworksIds: item.frameworksIds,
        })),
      };
      await postGapAnalysisVinculo(id, formattedData);
      navigate(`/gap-analysis/show/${id}`);

    } catch (error) {
      console.error("Erro ao cadastrar a análise:", error);
    }
  });


  const steps = [
    t('gap-analysis:controlsLink.steps.step1'),
    t('gap-analysis:controlsLink.steps.step2'),
    t('gap-analysis:controlsLink.steps.step3'),
  ];

  if (isLoading) return <p>{t('gap-analysis:controlsLink.loading')}</p>;
  if (error) return <p>{t('gap-analysis:controlsLink.error')}</p>;
  const controls = gapAnalysis.controls;
  const renderStep1 = () => {
    return (
      <Box display="flex" flexDirection="column" gap={3} padding={3}>
        <FormControl fullWidth data-tour="entity-type-select">
          <InputLabel id="select-label" shrink={true}>Elementos</InputLabel>
          <Select
            labelId="select-label"  notched label="Elementos"
            value={selectedOption}
            variant="outlined"
            onChange={(e) => setSelectedOption(e.target.value)}
         >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>


        {loading && <CircularProgress sx={{ mt: 2 }} />}
        <FormControl fullWidth margin="normal" error={!!errors.entity}>
          <div data-tour="entity-selector">
            <ItemSelector
              config={{
                title: "itens",
                idKey: "_id",
                nameKey: "name",
                descriptionKey: "description",
                formKey: "entity",
              }}
            items={items}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setValue={setValue}
            trigger={trigger}
            />
          </div>
          {errors.entity && <FormHelperText>{errors.entity.message}</FormHelperText>}

        </FormControl>
      </Box>
    );
  };
  const renderStep2 = () => {
    return (
      <ItemSelector
        config={{
          title: "Controles",
          idKey: "_id",
          nameKey: "scfControl",
          descriptionKey: "scfControlDescription",
          formKey: "controls",
        }}
        items={controls}
        searchTerm={controlSearchTerm}
        setSearchTerm={setControlSearchTerm}
        setValue={setValue}
        trigger={trigger}
      />
    );
  }

  const renderReview = () => {
    const selectedEntity = watch("entity") || [];
    const selectedControls = watch("controls") || [];

    return (
      <Box display="flex" flexDirection="column" gap={3} padding={3}>
        <Typography variant="h5">{t('gap-analysis:controlsLink.summary')}</Typography>

        {/* Resumo dos Vinculados */}
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="h6">{t('gap-analysis:controlsLink.selectedEntities')}</Typography>
          {selectedEntity.length > 0 ? (
            <List>
              {selectedEntity.map((asset, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={asset.name || `Ativo sem nome (${asset.id})`}
                    secondary={`Tipo: ${typeLabels[asset.type] || "Não especificado"}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="textSecondary">{t('gap-analysis:controlsLink.noEntitySelected')}</Typography>
          )}

          <Typography variant="h6">{t('gap-analysis:controlsLink.selectedControls')}</Typography>
          {selectedControls.length > 0 ? (
            <List>
              {selectedControls.map((control, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={control.scfControl || `Controle sem nome (${control.controlId})`}
                    secondary={control.scfControlDescription || "Sem descrição disponível"}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="textSecondary">{t('gap-analysis:controlsLink.noControlSelected')}</Typography>
          )}
        </Box>

        {/* Botão de Salvar */}
        <Button variant="contained" color="primary" onClick={submitVinculos}>
          {t('gap-analysis:controlsLink.saveButton')}
        </Button>
      </Box>
    );
  };



  const stepsContent = [
    renderStep1({ watch, errors, register}),
    renderStep2({ watch, errors, register}),
    renderReview({ watch }),
  ];


  return (
    <>
      {/* Helper Interativo */}
      <PageHelper 
        steps={tourSteps}
        tourKey="gap-analysis-controls-link-tour"
        autoStart={true}
        showButton={true}
      />
      
      <Container title={t('gap-analysis:controlsLink.title')}
                 breadcrumb={`${breadcrumbBase} / ${gapAnalysis.gapAnalysis?.name}`}
                 linkPage={-1}
      >


      <Card sx={'mt-4'}>
        <ProgressStepper
          steps={steps}
          activeStep={step}
          onNext={() => setStep((prev) => prev + 1)}
          onBack={() => setStep((prev) => prev - 1)}
          stepsContent={stepsContent} />
      </Card>

    </Container>
    </>
  )
}